import React, { useState, useRef } from "react";
import { Outlet } from "react-router-dom";
import {
  Button,
  TextInput,
  Select,
  Modal,
  Group,
  Loader,
  Stack,
  Table,
  ActionIcon,
  Alert,
  Card,
  Title,
  Text,
  Badge,
  Progress,
  Divider,
  Tooltip,
} from "@mantine/core";
import {
  IconUpload,
  IconRefresh,
  IconDownload,
  IconEye,
  IconMail,
  IconDatabase,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconEdit,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { CertificateGenerator } from "../components/CertificateGenerator";
import jsPDF from "jspdf";
import "../App.css";
import logo from "../images/think_cloudly_logo_transparent.png";
import CourseCompletion from "../components/CourseCompletion";
import SeminarCertificate from "../components/SeminarCertificate";

import { notifications } from "@mantine/notifications";

import { User } from "../common/models";
import { ApiService } from "../api/api-service";
import QRCode from "qrcode";

interface StudentData {
  Name: string;
  Email: string;
}

interface GeneratedCertificate {
  SerialNumber: number;
  Name: string;
  Email: string;
  EventName: string;
  EventType: string;
  StartDate: string;
  EndDate: string;
  ApprovedBy: string;
  CertificateNumber: string;
  cpeCount: string;
  cpeHours: string;
  country?: string;
}

interface EventData {
  eventName: string;
  eventType: string;
  startDate?: string;
  endDate?: string;
  completionDate?: string;
  approvedBy?: string;
  cpeCount?: string;
  cpeHours?: string;
  country?: string;
}

interface RawStudentRow {
  [key: string]: any;
}

const Certification: React.FC = () => {
  // Step management
  const [currentStep, setCurrentStep] = useState<
    "upload" | "form" | "generate" | "table"
  >("upload");

  // File upload states
  const [uploadedData, setUploadedData] = useState<StudentData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states - Always editable
  const [eventType, setEventType] = useState<string>("");
  const [eventName, setEventName] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [completionDate, setCompletionDate] = useState<string>("");
  const [cpeCount, setCpeCount] = useState<string>("");
  const [cpeHours, setCpeHours] = useState<string>("");

  // Generated certificates states
  const [generatedCertificates, setGeneratedCertificates] = useState<
    GeneratedCertificate[]
  >([]);

  const [country, setCountry] = useState<string>("");

  const [tempCountry, setTempCountry] = useState<string>("");

  // UI states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewedCertificate, setViewedCertificate] =
    useState<JSX.Element | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<"upload" | "send">(
    "upload",
  );
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Progress tracking
  const [progress, setProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>("");

  // Temporary states for editing
  const [tempEventType, setTempEventType] = useState<string>("");
  const [tempEventName, setTempEventName] = useState<string>("");
  const [tempCompletionDate, setTempCompletionDate] = useState<string>("");
  const [tempStartDate, setTempStartDate] = useState<string>("");
  const [tempCpeCount, setTempCpeCount] = useState<string>("");
  const [tempCpeHours, setTempCpeHours] = useState<string>("");

  // Add state for email logs and mail sending status
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [mailSendingComplete, setMailSendingComplete] =
    useState<boolean>(false);

  // NEW STATES FOR DATABASE AND EMAIL CONTROL
  const [isUploadedToDatabase, setIsUploadedToDatabase] =
    useState<boolean>(false);
  const [isEventDetailsLocked, setIsEventDetailsLocked] =
    useState<boolean>(false);

  const formatDateToMMDDYYYY = (dateString: string): string => {
    const [year, month, day] = dateString.split("-");
    return `${month}-${day}-${year}`;
  };
  const formatDateToDDMMYYYY = (dateString: string): string => {
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getSeminarDisplayName = (name: string): string => {
    return name?.trim() ? name : " ";
  };

  // Trim Spacing
  const formatName = (name: string): string => {
    if (!name) return "";

    // Remove extra spaces and split into words
    const words = name.trim().split(/\s+/);

    // Capitalize first letter of each word, lowercase the rest
    const formatted = words.map((word) => {
      if (word.length === 0) return "";
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

    // Join with single space
    return formatted.join(" ");
  };

  const normalizeCellValue = (value: unknown): string => {
    return String(value ?? "").trim();
  };

  const parseStudentRow = (row: RawStudentRow): StudentData => {
    const normalizedRow = Object.entries(row || {}).reduce<RawStudentRow>(
      (acc, [key, value]) => {
        acc[String(key).trim().toLowerCase()] = value;
        return acc;
      },
      {},
    );

    return {
      Name: formatName(normalizeCellValue(normalizedRow.name)),
      Email: normalizeCellValue(normalizedRow.email),
    };
  };

  const downloadCSVTemplate = () => {
    const csvData = `Name,Email`;
    const blob = new Blob([csvData], { type: "text/csv" });
    const href = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: href,
      style: "display:none",
      download: "certificate_template.csv",
    });
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(href);
    a.remove();
  };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files![0];
  //   if (!file) return;

  //   const reader = new FileReader();
  //   reader.onload = (event) => {
  //     try {
  //       const workbook = XLSX.read(event!.target!.result as string, {
  //         type: "binary",
  //       });
  //       const sheetName = workbook.SheetNames[0];
  //       const sheet = workbook.Sheets[sheetName];
  //       const sheetData = XLSX.utils.sheet_to_json(sheet) as StudentData[];

  //       // Validate data
  //       const validData = sheetData.filter(row => row.Name && row.Email);
  //       if (validData.length === 0) {
  //         notifications.show({
  //           title: "Invalid Data",
  //           message: "No valid student data found in the file",
  //           color: "red",
  //         });
  //         return;
  //       }

  //       setUploadedData(validData);
  //       setCurrentStep('form');

  //       notifications.show({
  //         title: "File Uploaded",
  //         message: `Successfully loaded ${validData.length} students`,
  //         color: "green",
  //       });
  //     } catch (error) {
  //       notifications.show({
  //         title: "Upload Error",
  //         message: "Failed to parse the file. Please check the format.",
  //         color: "red",
  //       });
  //     }
  //   };
  //   reader.readAsBinaryString(file);
  // };
  const COUNTRIES = [
    "Argentina",
    "Armenia",
    "Australia",
    "Austria",
    "Azerbaijan",
    "Bahamas",
    "Bahrain",
    "Bangladesh",
    "Barbados",
    "Belarus",
    "Belgium",
    "Belize",
    "Benin",
    "Bhutan",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Botswana",
    "Brazil",
    "Brunei",
    "Bulgaria",
    "Burkina Faso",
    "Burundi",
    "Cabo Verde",
    "Cambodia",
    "Cameroon",
    "Canada",
    "Central African Republic",
    "Chad",
    "Chile",
    "China",
    "Colombia",
    "Comoros",
    "Congo",
    "Costa Rica",
    "Croatia",
    "Cuba",
    "Cyprus",
    "Czech Republic",
    "Denmark",
    "Djibouti",
    "Dominica",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Equatorial Guinea",
    "Eritrea",
    "Estonia",
    "Eswatini",
    "Ethiopia",
    "Fiji",
    "Finland",
    "France",
    "Gabon",
    "Gambia",
    "Georgia",
    "Germany",
    "Ghana",
    "Greece",
    "Grenada",
    "Guatemala",
    "Guinea",
    "Guinea-Bissau",
    "Guyana",
    "Haiti",
    "Honduras",
    "Hungary",
    "Iceland",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Ireland",
    "Israel",
    "Italy",
    "Jamaica",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kenya",
    "Kiribati",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Latvia",
    "Lebanon",
    "Lesotho",
    "Liberia",
    "Libya",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Madagascar",
    "Malawi",
    "Malaysia",
    "Maldives",
    "Mali",
    "Malta",
    "Marshall Islands",
    "Mauritania",
    "Mauritius",
    "Mexico",
    "Micronesia",
    "Moldova",
    "Monaco",
    "Mongolia",
    "Montenegro",
    "Morocco",
    "Mozambique",
    "Myanmar",
    "Namibia",
    "Nauru",
    "Nepal",
    "Netherlands",
    "New Zealand",
    "Nicaragua",
    "Niger",
    "Nigeria",
    "North Korea",
    "North Macedonia",
    "Norway",
    "Oman",
    "Pakistan",
    "Palau",
    "Palestine",
    "Panama",
    "Papua New Guinea",
    "Paraguay",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "Qatar",
    "Romania",
    "Russia",
    "Rwanda",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Samoa",
    "San Marino",
    "Sao Tome and Principe",
    "Saudi Arabia",
    "Senegal",
    "Serbia",
    "Seychelles",
    "Sierra Leone",
    "Singapore",
    "Slovakia",
    "Slovenia",
    "Solomon Islands",
    "Somalia",
    "South Africa",
    "South Korea",
    "South Sudan",
    "Spain",
    "Sri Lanka",
    "Sudan",
    "Suriname",
    "Sweden",
    "Switzerland",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Tanzania",
    "Thailand",
    "Timor-Leste",
    "Togo",
    "Tonga",
    "Trinidad and Tobago",
    "Tunisia",
    "Turkey",
    "Turkmenistan",
    "Tuvalu",
    "Uganda",
    "Ukraine",
    "United Arab Emirates",
    "United Kingdom",
    "United States",
    "Uruguay",
    "Uzbekistan",
    "Vanuatu",
    "Vatican City",
    "Venezuela",
    "Vietnam",
    "Yemen",
    "Zambia",
    "Zimbabwe",
  ];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event!.target!.result as string, {
          type: "binary",
        });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
          raw: false,
        }) as RawStudentRow[];

        // Validate and format data — both Name and Email are required
        const validData = sheetData
          .map(parseStudentRow)
          .filter((row) => row.Name && row.Email);

        if (validData.length === 0) {
          notifications.show({
            title: "Invalid Data",
            message: "No valid student data found in the file",
            color: "red",
          });
          return;
        }

        setUploadedData(validData);
        setCurrentStep("form");

        notifications.show({
          title: "File Uploaded",
          message: `Successfully loaded ${validData.length} students`,
          color: "green",
        });
      } catch (error) {
        notifications.show({
          title: "Upload Error",
          message: "Failed to parse the file. Please check the format.",
          color: "red",
        });
      }
    };
    reader.readAsBinaryString(file);
  };
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateForm = (): boolean => {
    if (!eventType.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Event Type is required",
        color: "red",
      });
      return false;
    }

    if (!eventName.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Event Name is required",
        color: "red",
      });
      return false;
    }

    if (!completionDate.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Completion Date is required",
        color: "red",
      });
      return false;
    }

    if (
      (eventType === "BOOTCAMP" ||
        eventType === "COURSE COMPLETION" ||
        eventType === "BOOTCAMP-CPE" ||
         eventType === "CAPSULE COURSE") &&
      !startDate.trim()
    ) {
      notifications.show({
        title: "Validation Error",
        message: "Start Date is required for Bootcamp or Course Completion",
        color: "red",
      });
      return false;
    }

    // Validate CPE fields for BOOTCAMP-CPE
    if (eventType === "BOOTCAMP-CPE"|| eventType === "CAPSULE COURSE") {
      if (!cpeCount.trim()) {
        notifications.show({
          title: "Validation Error",
          message: "CPE Count is required for BOOTCAMP-CPE",
          color: "red",
        });
        return false;
      }

      if (!cpeHours.trim()) {
        notifications.show({
          title: "Validation Error",
          message: "CPE Hours is required for BOOTCAMP-CPE",
          color: "red",
        });
        return false;
      }
    }
    if (eventType === "SEMINAR" && !country.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Country is required for Seminar",
        color: "red",
      });
      return false;
    }
    return true;
  };

  const handleGenerateCertificates = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setCurrentStep("generate");

    try {
      const eventData: EventData = {
        eventName,
        eventType,
        startDate,
        endDate: completionDate,
        completionDate,
        approvedBy: "Think Cloudly",

        cpeCount: cpeCount,
        cpeHours: cpeHours,
        country: country,
      };

      // Log event data and a sample of uploaded students for debugging CPE propagation
      // console.log('handleGenerateCertificates - eventData:', eventData);
      // console.log('handleGenerateCertificates - uploadedData (sample):', uploadedData.slice(0, 5));

      // Call backend to generate certificates
      const response = await ApiService.getInstance().post(
        "/certificate/generate",
        {
          students: uploadedData,
          eventData,
        },
      );

      if (response.data.success) {
        // Ensure each generated certificate includes cpeCount and cpeHours
        const certificatesWithCpe = (response.data.certificates || []).map(
          (cert: any) => ({
            ...cert,
            cpeCount: cert.cpeCount ?? cpeCount,
            cpeHours: cert.cpeHours ?? cpeHours,
          }),
        );
        setGeneratedCertificates(certificatesWithCpe);

        // Debug: show certificates (first 10) and ensure cpe fields are present
        // console.log('handleGenerateCertificates - generated certificates (with cpe):', certificatesWithCpe.slice(0, 10));
        setCurrentStep("table");

        // Reset database and email states for new certificates
        setIsUploadedToDatabase(false);
        setIsEventDetailsLocked(false);
        setEmailLogs([]);
        setMailSendingComplete(false);

        notifications.show({
          title: "Certificates Generated",
          message: `Successfully generated ${response.data.generated} certificates`,
          color: "green",
        });
      } else {
        throw new Error(
          response.data.error || "Failed to generate certificates",
        );
      }
    } catch (error: any) {
      notifications.show({
        title: "Generation Error",
        message: error.message || "Failed to generate certificates",
        color: "red",
      });
      setCurrentStep("form");
    } finally {
      setIsLoading(false);
    }
  };

  const generateQRCode = async (
    certificateNumber: string,
  ): Promise<string | null> => {
    try {
      const verificationUrl = `https://thinkcloudly.com/cert-verification?cert=${certificateNumber}`;
      const qr = await QRCode.toDataURL(verificationUrl, {
        width: 200,
        margin: 1,
        color: {
          dark: "#221E45",
          light: "#ffffff",
        },
      });
      return qr;
    } catch (error) {
      console.error("Error generating QR code:", error);
      return null;
    }
  };

  const generatePDF = async (
    certificateData: GeneratedCertificate,
  ): Promise<jsPDF> => {
    return new Promise(async (resolve, reject) => {
      const doc = new jsPDF({
        orientation: "landscape",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const formattedStartDate = startDate
        ? formatDateToMMDDYYYY(startDate)
        : "";
      const formattedCompletionDate = completionDate
        ? formatDateToMMDDYYYY(completionDate)
        : "";
      const formattedCompletionDateDDMM = completionDate
        ? formatDateToDDMMYYYY(completionDate)
        : "";

      const qrCodeImage = await generateQRCode(
        certificateData.CertificateNumber,
      );
      // Generate QR Code
      generateQRCode(certificateData.CertificateNumber)
        .then((qrCodeImage) => {
          if (eventType === "COURSE COMPLETION") {
            // Use the Course Completion template
            doc.addImage(
              "https://ik.imagekit.io/2fljt9khj/1.png?updatedAt=1749652978442",
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight,
            );

            // Add QR Code
            if (qrCodeImage) {
              doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 25, 25);
            }

            // Certificate content
            doc.setTextColor(34, 30, 69); // #221E45

            // Name
            doc.setFontSize(40);
            doc.setFont("helvetica", "bold");
            doc.text(`${certificateData.Name}`, pageWidth / 2, 112, {
              align: "center",
            });

            // Course name
            doc.setFontSize(15);
            doc.setFont("helvetica", "bold");
            doc.text(`${eventName}`, pageWidth / 2, 135, {
              align: "center",
            });

            // Duration
            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.text(
              `${formattedStartDate} To ${formattedCompletionDate}`,
              pageWidth / 2 + 23,
              145,
              { align: "center" },
            );

            // Certificate details at bottom left
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.text(
              `Issued On: ${formattedCompletionDate}`,
              16,
              pageHeight - 12,
            );

            doc.setFontSize(11);
            doc.text(
              `Certificate No: ${certificateData.CertificateNumber}`,
              8,
              pageHeight - 8,
            );
          } else if (eventType === "BOOTCAMP") {
            // Use the Bootcamp template
            doc.addImage(
              "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight,
            );

            // Add QR Code
            if (qrCodeImage) {
              doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 26, 25);
            }

            // Certificate content
            doc.setTextColor(34, 30, 69);

            // "THIS CERTIFICATE IS PROUDLY PRESENTED TO"
            doc.setFontSize(22);
            doc.setFont("helvetica", "normal");
            doc.text(
              "THIS CERTIFICATE IS PROUDLY PRESENTED TO",
              pageWidth / 2,
              85,
              {
                align: "center",
              },
            );

            // Name
            doc.setFontSize(30);
            doc.setFont("helvetica", "bold");
            doc.text(`${certificateData.Name}`, pageWidth / 2, 100, {
              align: "center",
            });

            // Event description
            doc.setFontSize(21);
            doc.setFont("helvetica", "normal");
            doc.text(
              `TO BE A PART OF THE ${eventType} ON`,
              pageWidth / 2,
              115,
              {
                align: "center",
              },
            );

            // Event name
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.text(`${eventName}`, pageWidth / 2, 125, {
              align: "center",
            });

            // Duration
            doc.setFontSize(15);
            doc.setFont("helvetica", "normal");
            doc.text(
              `Held From ${formattedStartDate} To ${formattedCompletionDate}.`,
              pageWidth / 2,
              135,
              { align: "center" },
            );

            // Certificate details
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.text(
              `Issued On: ${formattedCompletionDate}`,
              16,
              pageHeight - 12,
            );

            doc.setFontSize(11);
            doc.text(
              `Certificate No: ${certificateData.CertificateNumber}`,
              8,
              pageHeight - 8,
            );
          } else if (eventType === "BOOTCAMP-CPE") {
            // BOOTCAMP-CPE template (same visual as Bootcamp with CPE specific text)
            doc.addImage(
              "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight,
            );

            // Add QR Code
            if (qrCodeImage) {
              doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 26, 25);
            }

            // Certificate content
            // Use special color for the Name (hex #FC7A69)
            doc.setTextColor(252, 122, 105);

            // Dynamic layout variables for consistent spacing relative to the name
            const nameFontSize = 38;
            const attendedFontSize = 19;
            const eventFontSize = 23;
            const cpeFontSize = 19;
            const heldFontSize = 16;
            const nameY = 80; // baseline for the name

            // calculate positions so spacing is proportional to font sizes
            const attendedY = nameY + Math.round(nameFontSize * 0.6); // gap under name
            const eventY = attendedY + attendedFontSize + 4;
            const cpeY = eventY + Math.round(eventFontSize * 0.6) + 4;
            const heldY = cpeY + cpeFontSize + 6;

            // Name (Helvetica, bold, 30)
            doc.setFontSize(nameFontSize);
            doc.setFont("helvetica", "bold");
            doc.text(`${certificateData.Name}`, pageWidth / 2, nameY + 3, {
              align: "center",
            });

            // Reset to default dark color for following lines
            doc.setTextColor(34, 30, 69);

            // Small sentence: has attended the Bootcamp on (font size 19)
            doc.setFontSize(attendedFontSize);
            doc.setFont("helvetica", "normal");
            doc.text(`Has attended the Bootcamp on`, pageWidth / 2, attendedY, {
              align: "center",
            });

            // Event name (e.g., AWS, GRC) - larger (23)
            doc.setFontSize(eventFontSize);
            doc.setFont("helvetica", "bold");
            doc.text(`${eventName}`, pageWidth / 2, eventY - 12, {
              align: "center",
            });

            // CPE line
            doc.setFontSize(cpeFontSize);
            doc.setFont("helvetica", "normal");
            // Use per-certificate CPE values when available
            const perCertCpeCount = certificateData.cpeCount || cpeCount || "0";
            const perCertCpeHours = certificateData.cpeHours || cpeHours || "0";
            const cpeText = `And is entitled with ${perCertCpeCount} CPE for ${perCertCpeHours} of Training`;
            doc.text(cpeText, pageWidth / 2, cpeY - 20, { align: "center" });

            // Duration (held line)
            doc.setFontSize(heldFontSize);
            doc.setFont("helvetica", "normal");
            doc.text(
              `Held From ${formattedStartDate} To ${formattedCompletionDate}.`,
              pageWidth / 2,
              heldY - 20,
              { align: "center" },
            );

            // Certificate details
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.text(
              `Issued On: ${formattedCompletionDate}`,
              16,
              pageHeight - 12,
            );

            doc.setFontSize(11);
            doc.text(
              `Certificate No: ${certificateData.CertificateNumber}`,
              8,
              pageHeight - 8,
            );
            
          // } else if (eventType === "CAPSULE COURSE") {
          //   // BOOTCAMP-CPE template (same visual as Bootcamp with CPE specific text)
          //   doc.addImage(
          //     "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
          //     "PNG",
          //     0,
          //     0,
          //     pageWidth,
          //     pageHeight,
          //   );

          //   // Add QR Code
          //   if (qrCodeImage) {
          //     doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 26, 25);
          //   }

          //   // Certificate content
          //   // Use special color for the Name (hex #FC7A69)
          //   doc.setTextColor(252, 122, 105);

          //   // Dynamic layout variables for consistent spacing relative to the name
          //   const nameFontSize = 38;
          //   const attendedFontSize = 19;
          //   const eventFontSize = 23;
          //   const cpeFontSize = 19;
          //   const heldFontSize = 16;
          //   const nameY = 80; // baseline for the name

          //   // calculate positions so spacing is proportional to font sizes
          //   const attendedY = nameY + Math.round(nameFontSize * 0.6); // gap under name
          //   const eventY = attendedY + attendedFontSize + 4;
          //   const cpeY = eventY + Math.round(eventFontSize * 0.6) + 4;
          //   const heldY = cpeY + cpeFontSize + 6;

          //   // Name (Helvetica, bold, 30)
          //   doc.setFontSize(nameFontSize);
          //   doc.setFont("helvetica", "bold");
          //   doc.text(`${certificateData.Name}`, pageWidth / 2, nameY + 3, {
          //     align: "center",
          //   });

          //   // Reset to default dark color for following lines
          //   doc.setTextColor(34, 30, 69);

          //   // Small sentence: has attended the Bootcamp on (font size 19)
          //   doc.setFontSize(attendedFontSize);
          //   doc.setFont("helvetica", "normal");
          //   // doc.text(`Has attended the Bootcamp on`, pageWidth / 2, attendedY, {
          //   doc.text(`Has attended the MasterClass on`, pageWidth / 2, attendedY,
          //      { align: "center" });

          //   // Event name (e.g., AWS, GRC) - larger (23)
          //   doc.setFontSize(eventFontSize);
          //   doc.setFont("helvetica", "bold");
          //   doc.text(`${eventName}`, pageWidth / 2, eventY - 12, {
          //     align: "center",
          //   });

          //   // CPE line
          //   doc.setFontSize(cpeFontSize);
          //   doc.setFont("helvetica", "normal");
          //   // Use per-certificate CPE values when available
          //   const perCertCpeCount = certificateData.cpeCount || cpeCount || "0";
          //   const perCertCpeHours = certificateData.cpeHours || cpeHours || "0";
          //   const cpeText = `And is entitled with ${perCertCpeCount} CPE for ${perCertCpeHours} of Training`;
          //   doc.text(cpeText, pageWidth / 2, cpeY - 20, { align: "center" });

          //   // Duration (held line)
          //   doc.setFontSize(heldFontSize);
          //   doc.setFont("helvetica", "normal");
          //   doc.text(
          //     `Held From ${formattedStartDate} To ${formattedCompletionDate}.`,
          //     pageWidth / 2,
          //     heldY - 20,
          //     { align: "center" },
          //   );

          //   // Certificate details
          //   doc.setTextColor(51, 51, 51);
          //   doc.setFontSize(10);
          //   doc.text(
          //     `Issued On: ${formattedCompletionDate}`,
          //     16,
          //     pageHeight - 12,
          //   );

          //   doc.setFontSize(11);
          //   doc.text(
          //     `Certificate No: ${certificateData.CertificateNumber}`,
          //     8,
          //     pageHeight - 8,
          //   );
          // }
// } else if (eventType === "CAPSULE COURSE") {
//   doc.addImage(
//     "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
//     "PNG", 0, 0, pageWidth, pageHeight,
//   );

//   // ─── Typography scale ──────────────────────────────────────────────────────
//   // "times" is built-in to jsPDF — elegant serif, ideal for certificates
//   // Name uses a larger italic-bold combo for a premium feel
//   const NAME_SIZE      = 32;
//   const ATTENDED_SIZE  = 14;
//   const EVENT_SIZE     = 19;
//   const CPE_SIZE       = 13;
//   const HELD_SIZE      = 12;

//   // ─── Vertical rhythm (% of page height) ───────────────────────────────────
//   // Ribbon bottom is ~34% — name starts at 40% for clear breathing room
//   const nameY      = pageHeight * 0.41;  // below ribbon with generous gap
//   const attendedY  = pageHeight * 0.51;  // ~10% gap creates visual pause
//   const eventY     = pageHeight * 0.57;  // tighter to "attended" line (related)
//   const cpeY       = pageHeight * 0.63;  // consistent rhythm
//   const heldY      = pageHeight * 0.68;  // consistent rhythm
//   // Signature strip begins ~73% — safe margin above it

//   // ─── Name — serif bold-italic for premium look ────────────────────────────
//   doc.setFontSize(NAME_SIZE);
//   doc.setFont("times", "bolditalic");        // elegant serif, built-in to jsPDF
//   doc.setTextColor(252, 122, 105);           // brand coral
//   doc.text(certificateData.Name, pageWidth / 2, nameY, { align: "center" });

//   // ─── "Has attended" — light serif normal ──────────────────────────────────
//   doc.setTextColor(34, 30, 69);
//   doc.setFontSize(ATTENDED_SIZE);
//   doc.setFont("times", "normal");
//   doc.text("Has attended the MasterClass on", pageWidth / 2, attendedY, {
//     align: "center",
//   });

//   // ─── Event name — sans-serif bold for contrast ────────────────────────────
//   doc.setFontSize(EVENT_SIZE);
//   doc.setFont("helvetica", "bold");
//   doc.text(eventName, pageWidth / 2, eventY, { align: "center" });

//   // ─── CPE line — serif normal ───────────────────────────────────────────────
//   const perCertCpeCount = certificateData.cpeCount || cpeCount || "0";
//   const perCertCpeHours = certificateData.cpeHours || cpeHours || "0";
//   const hoursValue = String(perCertCpeHours).replace(/\s*hours?\s*/gi, "").trim();
//   const cpeText = `And is entitled with ${perCertCpeCount} CPE for ${hoursValue} Hours of Training`;

//   doc.setFontSize(CPE_SIZE);
//   doc.setFont("times", "normal");
//   doc.text(cpeText, pageWidth / 2, cpeY, { align: "center" });

//   // ─── Held From — italic for subtle distinction ────────────────────────────
//   doc.setFontSize(HELD_SIZE);
//   doc.setFont("times", "italic");
//   doc.text(
//     `Held From ${formattedStartDate} To ${formattedCompletionDate}.`,
//     pageWidth / 2, heldY,
//     { align: "center" }
//   );

//   // ─── QR code ───────────────────────────────────────────────────────────────
//   const QR_SIZE = 24;
//   const QR_LEFT = 14;
//   const QR_TOP  = pageHeight * 0.78;

//   if (qrCodeImage) {
//     doc.addImage(qrCodeImage, "PNG", QR_LEFT, QR_TOP, QR_SIZE, QR_SIZE);
//   }

//   // ─── Footer ────────────────────────────────────────────────────────────────
//   doc.setTextColor(100, 100, 100);
//   doc.setFont("helvetica", "normal");
//   doc.setFontSize(8);
//   doc.text(`Issued On: ${formattedCompletionDate}`,                QR_LEFT, pageHeight * 0.93);
//   doc.text(`Certificate No: ${certificateData.CertificateNumber}`, QR_LEFT, pageHeight * 0.97);
// } 
} else if (eventType === "CAPSULE COURSE") {
  doc.addImage(
    "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
    "PNG", 0, 0, pageWidth, pageHeight,
  );

  // ─── Typography scale ──────────────────────────────────────────────────────
  const NAME_SIZE     = 38;  // Large, commanding presence
  const ATTENDED_SIZE = 16;  // Comfortable reading size
  const EVENT_SIZE    = 22;  // Bold, prominent course name
  const CPE_SIZE      = 15;  // Clear supporting text
  const HELD_SIZE     = 14;  // Subtle date line

  // ─── Vertical positions (% of page height) ────────────────────────────────
  // Template ribbon ends ~33% | Signature strip begins ~72%
  // Usable content zone: 33% → 72% = 39% of page height
  // We centre the 5-line block inside that zone.
  const nameY     = pageHeight * 0.40;  // anchor — just below ribbon
  const attendedY = pageHeight * 0.50;  // spacious gap creates visual pause
  const eventY    = pageHeight * 0.57;  // close to "attended" (related context)
  const cpeY      = pageHeight * 0.63;  // even rhythm below event
  const heldY     = pageHeight * 0.69;  // last content line, clear of signatures

  // ─── Decorative rule under name ───────────────────────────────────────────
  // Thin centred line between name and body copy — classic certificate detail
  const ruleY   = nameY + 6;
  const ruleLen = 55;
  doc.setDrawColor(252, 122, 105);   // coral — matches name colour
  doc.setLineWidth(0.4);
  doc.line(
    pageWidth / 2 - ruleLen,
    ruleY,
    pageWidth / 2 + ruleLen,
    ruleY,
  );

  // ─── Recipient name ────────────────────────────────────────────────────────
  doc.setFontSize(NAME_SIZE);
  doc.setFont("times", "bolditalic");   // prestigious serif
  doc.setTextColor(252, 122, 105);      // brand coral
  doc.text(certificateData.Name, pageWidth / 2, nameY, { align: "center" });

  // ─── Body copy (dark navy) ─────────────────────────────────────────────────
  doc.setTextColor(34, 30, 69);

  // "Has attended the MasterClass on"
  doc.setFontSize(ATTENDED_SIZE);
  doc.setFont("times", "normal");
  doc.text(
    "Has attended the Masterclass on",
    pageWidth / 2,
    attendedY,
    { align: "center" },
  );

  // Event name — sans-serif bold for strong visual contrast vs serif body
  doc.setFontSize(EVENT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text(eventName, pageWidth / 2, eventY, { align: "center" });

  // Thin rule under event name — mirrors rule under name
  const eventRuleY = eventY + 4;
  doc.setDrawColor(34, 30, 69);
  doc.setLineWidth(0.3);
  doc.line(
    pageWidth / 2 - 40,
    eventRuleY,
    pageWidth / 2 + 40,
    eventRuleY,
  );

  // CPE entitlement
  const perCertCpeCount = certificateData.cpeCount || cpeCount || "0";
  const perCertCpeHours = certificateData.cpeHours || cpeHours || "0";
  const hoursValue = String(perCertCpeHours)
    .replace(/\s*hours?\s*/gi, "")
    .trim();
  const cpeText = `And is entitled with ${perCertCpeCount} CPE for ${hoursValue} Hours of Training`;

  doc.setFontSize(CPE_SIZE);
  doc.setFont("times", "normal");
  doc.text(cpeText, pageWidth / 2, cpeY, { align: "center" });

  // Held From — italic for subtle distinction
  doc.setFontSize(HELD_SIZE);
  doc.setFont("times", "italic");
  doc.setTextColor(80, 80, 80);   // slightly muted — secondary info
  doc.text(
    `Held From ${formattedStartDate} To ${formattedCompletionDate}.`,
    pageWidth / 2,
    heldY,
    { align: "center" },
  );

  // ─── QR code — bottom-left ─────────────────────────────────────────────────
  const QR_SIZE = 26;
  const QR_LEFT = 14;
  const QR_TOP  = pageHeight * 0.77;

  if (qrCodeImage) {
    doc.addImage(qrCodeImage, "PNG", QR_LEFT, QR_TOP, QR_SIZE, QR_SIZE);
  }

  // ─── Footer metadata ───────────────────────────────────────────────────────
  doc.setTextColor(110, 110, 110);
  doc.setFont("helvetica", "normal");

  doc.setFontSize(8.5);
  // doc.text(
  //   `Issued On: ${formattedCompletionDate}`,
  //   QR_LEFT,
  //   pageHeight * 0.92,
  // );

  // doc.setFontSize(8.5);
  // doc.text(
  //   `Certificate No: ${certificateData.CertificateNumber}`,
  //   QR_LEFT,
  //   pageHeight * 0.96,
  // );
  
  
const footerY1 = QR_TOP + QR_SIZE + 3.5;   // first line: 5pt below QR bottom
  const footerY2 = footerY1 + 3.5;            // second line: 5pt below first

  doc.text(`Issued On: ${formattedCompletionDate}`,                QR_LEFT, footerY1);
  doc.text(`Certificate No: ${certificateData.CertificateNumber}`, QR_LEFT, footerY2);
}
          
           else if (eventType === "SEMINAR") {
            // SEMINAR template — same background as Bootcamp/Webinar
            doc.addImage(
              "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight,
            );

            if (qrCodeImage) {
              doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 25, 25);
            }

            doc.setTextColor(34, 30, 69);

            doc.setFontSize(23);
            doc.setFont("helvetica", "normal");
            doc.text(
              "THIS CERTIFICATE IS PROUDLY PRESENTED TO",
              pageWidth / 2,
              85,
              { align: "center" },
            );

            doc.setFontSize(25);
            doc.setFont("helvetica", "bold");
            doc.text(
              getSeminarDisplayName(certificateData.Name),
              pageWidth / 2,
              100,
              {
                align: "center",
              },
            );

            doc.setFontSize(21);
            doc.setFont("helvetica", "normal");
            // doc.text(`TO BE A PART OF THE SEMINAR ON`, pageWidth / 2, 115, {
            //   align: "center",
            // });
            // doc.text(`FOR PARTICIPATING IN THE`, pageWidth / 2, 115, {
            //   align: "center",
            // });

            // doc.setFontSize(18);
            // doc.setFont("helvetica", "bold");
            // doc.text(`${eventName}`, pageWidth / 2, 125, { align: "center" });

            // doc.setFontSize(15);
            // doc.setFont("helvetica", "normal");
            // // const locationText = country
            // //   ? `Held On ${formattedCompletionDate} IN ${country.toUpperCase()}.`
            // //   : `Held On ${formattedCompletionDate}.`;
            // const locationText = country
            //   ? `HELD ON ${formattedCompletionDate} IN ${country.toUpperCase()}.`
            //   : `HELD ON ${formattedCompletionDate}.`;
            // doc.text(locationText, pageWidth / 2, 135, { align: "center" });

            // doc.setTextColor(51, 51, 51);
            // doc.setFontSize(10);
            // doc.text(
            //   `Issued On: ${formattedCompletionDate}`,
            //   16,
            //   pageHeight - 12,
            // );
            doc.setFontSize(15);
            doc.setFont("helvetica", "normal");
            doc.text(`FOR PARTICIPATING IN THE`, pageWidth / 2, 113, {
              align: "center",
            });

            doc.setFontSize(17);
            doc.setFont("helvetica", "bold");
            doc.text(`${eventName}`, pageWidth / 2, 122, { align: "center" });

            // Decorative line under event name
            doc.setDrawColor(232, 99, 74);
            doc.setLineWidth(0.8);
            doc.line(pageWidth / 2 - 20, 125, pageWidth / 2 + 20, 125);

            doc.setFontSize(15);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(80, 80, 80);
            const locationText = country
              ? `Held On ${formattedCompletionDateDDMM} In ${country}.`
              : `Held On ${formattedCompletionDateDDMM}.`;
            doc.text(locationText, pageWidth / 2, 132, { align: "center" });

            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.text(
              `Issued On: ${formattedCompletionDateDDMM}`,
              16,
              pageHeight - 12,
            );

            doc.setFontSize(11);
            doc.text(
              `Certificate No: ${certificateData.CertificateNumber}`,
              8,
              pageHeight - 8,
            );
          } else {
            // WEBINAR template
            doc.addImage(
              "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627",
              "PNG",
              0,
              0,
              pageWidth,
              pageHeight,
            );

            // Add QR Code
            if (qrCodeImage) {
              doc.addImage(qrCodeImage, "PNG", 20, pageHeight - 40, 25, 25);
            }

            // Certificate content
            doc.setTextColor(34, 30, 69);

            // "THIS CERTIFICATE IS PROUDLY PRESENTED TO"
            doc.setFontSize(23);
            doc.setFont("helvetica", "normal");
            doc.text(
              "THIS CERTIFICATE IS PROUDLY PRESENTED TO",
              pageWidth / 2,
              85,
              {
                align: "center",
              },
            );

            // Name
            doc.setFontSize(25);
            doc.setFont("helvetica", "bold");
            doc.text(`${certificateData.Name}`, pageWidth / 2, 100, {
              align: "center",
            });

            // Event description
            doc.setFontSize(21);
            doc.setFont("helvetica", "normal");
            doc.text(
              `TO BE A PART OF THE ${eventType} ON`,
              pageWidth / 2,
              115,
              {
                align: "center",
              },
            );

            // Event name
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.text(`${eventName}`, pageWidth / 2, 125, {
              align: "center",
            });

            // Date
            doc.setFontSize(15);
            doc.setFont("helvetica", "normal");
            doc.text(
              `Held On ${formattedCompletionDate}.`,
              pageWidth / 2,
              135,
              { align: "center" },
            );

            // Certificate details
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.text(
              `Issued On: ${formattedCompletionDate}`,
              16,
              pageHeight - 12,
            );

            doc.setFontSize(11);
            doc.text(
              `Certificate No: ${certificateData.CertificateNumber}`,
              8,
              pageHeight - 8,
            );
          }

          resolve(doc);
        })
        .catch((error) => {
          console.error("Error generating PDF:", error);
          reject(error);
        });
    });
  };

  const handleViewCertificate = (certificateData: GeneratedCertificate) => {
    const certificateElement =
      eventType === "COURSE COMPLETION" ? (
        <CourseCompletion
          name={certificateData.Name}
          courseName={eventName}
          startDate={startDate ? formatDateToMMDDYYYY(startDate) : ""}
          completionDate={formatDateToMMDDYYYY(completionDate)}
          certificateNumber={certificateData.CertificateNumber}
        />
      ) : eventType === "SEMINAR" ? ( // ← ADD THIS BRANCH
        <SeminarCertificate
          name={certificateData.Name}
          eventName={eventName}
          completionDate={completionDate}
          country={country}
          certificateNumber={certificateData.CertificateNumber}
        />
      ) : (
        <CertificateGenerator
          name={certificateData.Name}
          eventName={eventName}
          eventType={eventType}
          startDate={formatDateToMMDDYYYY(startDate)}
          completionDate={formatDateToMMDDYYYY(completionDate)}
          certificateNumber={certificateData.CertificateNumber}
        />
      );

    setViewedCertificate(certificateElement);
  };

  const handleDownloadCertificate = async (
    certificateData: GeneratedCertificate,
  ) => {
    // console.log('Download certificate for:', certificateData.Name);
    const doc = generatePDF(certificateData);
    (await doc).save(`${certificateData.Name}_Certificate.pdf`);
  };

  const handleSendEmail = async (certificateData: GeneratedCertificate) => {
    if (!isUploadedToDatabase) {
      notifications.show({
        title: "Database Upload Required",
        message: "Please upload certificates to database before sending emails",
        color: "orange",
      });
      return;
    }

    if (!eventName.trim() || !completionDate.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Event Name and Completion Date are required",
        color: "red",
      });
      return;
    }

    if (
      (eventType === "BOOTCAMP" ||
        eventType === "COURSE COMPLETION" ||
        eventType === "BOOTCAMP-CPE" || eventType==="CAPSULE COURSE") &&
      !startDate.trim()
    ) {
      notifications.show({
        title: "Validation Error",
        message: "Start Date is required for Bootcamp or Course Completion",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    // console.log("Loading state set to true");

    try {
      // console.log("Generating PDF for certificate:", certificateData);
      const doc = await generatePDF(certificateData);
      const pdfBase64 = doc.output("datauristring").split(",")[1];
      // console.log("PDF generated successfully");

      const emailData = {
        email: certificateData.Email,
        pdfData: pdfBase64,
        name: certificateData.Name,
        eventName: eventType,
        webinarr: eventName,
        certificateNumber: certificateData.CertificateNumber,
      };

      const response = await ApiService.getInstance().post("/email", emailData);

      if (response.data && response.data.message === "Email sent") {
        notifications.show({
          title: "Certificate Sent",
          message: `Certificate sent successfully to ${certificateData.Name} (${certificateData.Email})`,
          color: "green",
          autoClose: 2000,
        });

        // Update email logs
        setEmailLogs((prev) => [
          ...prev,
          {
            name: certificateData.Name,
            email: certificateData.Email,
            status: "Sent",
            messageId: response.data.info?.messageId,
          },
        ]);
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error: any) {
      console.error(`Failed to send email to ${certificateData.Email}`, error);
      notifications.show({
        title: "Email Failed",
        message: `Failed to send certificate to ${certificateData.Name} (${certificateData.Email})`,
        color: "red",
        autoClose: 5000,
      });

      // Update email logs with failure
      setEmailLogs((prev) => [
        ...prev,
        {
          name: certificateData.Name,
          email: certificateData.Email,
          status: "Failed",
          error: error.message,
        },
      ]);
    } finally {
      setIsLoading(false);
      // console.log("Loading state set to false");
    }
  };

  const handleBulkEmailSend = async () => {
    if (!isUploadedToDatabase) {
      notifications.show({
        title: "Database Upload Required",
        message: "Please upload certificates to database before sending emails",
        color: "orange",
      });
      return;
    }

    if (!eventName.trim() || !completionDate.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "Event Name and Completion Date are required",
        color: "red",
      });
      return;
    }

    if (
      (eventType === "BOOTCAMP" ||
        eventType === "COURSE COMPLETION" ||
        eventType === "BOOTCAMP-CPE"|| eventType === "CAPSULE COURSE") &&
      !startDate.trim()
    ) {
      notifications.show({
        title: "Validation Error",
        message: "Start Date is required for Bootcamp or Course Completion",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setProgressMessage("Sending certificates via email...");

    try {
      const totalEmails = generatedCertificates.length;
      let successCount = 0;
      let failCount = 0;
      const logs = [];

      // Show initial notification
      notifications.show({
        id: "bulk-send-progress",
        title: "Sending Certificates",
        message: `Starting to send ${totalEmails} certificates...`,
        color: "blue",
        autoClose: false,
        loading: true,
      });

      for (let i = 0; i < generatedCertificates.length; i++) {
        const cert = generatedCertificates[i];
        // console.log(`Preparing to send email ${i + 1} of ${totalEmails}`);

        try {
          const doc = await generatePDF(cert);
          const pdfBase64 = doc.output("datauristring").split(",")[1];

          // Prepare email data
          const emailData = {
            email: cert.Email,
            pdfData: pdfBase64,
            name: cert.Name,
            eventName: eventType,
            webinarr: eventName,
            certificateNumber: cert.CertificateNumber,
          };

          // Send email
          const response = await ApiService.getInstance().post(
            "/email",
            emailData,
          );

          if (response.data && response.data.message === "Email sent") {
            successCount++;
            logs.push({
              name: cert.Name,
              email: cert.Email,
              status: "Sent",
              messageId: response.data.info?.messageId,
            });

            // Update progress notification
            notifications.update({
              id: "bulk-send-progress",
              title: "Sending Certificates",
              message: `Sent ${successCount} of ${totalEmails} certificates`,
              color: successCount === totalEmails ? "green" : "blue",
              loading: successCount < totalEmails,
              autoClose: successCount === totalEmails ? 5000 : false,
            });
          } else {
            failCount++;
            logs.push({
              name: cert.Name,
              email: cert.Email,
              status: "Failed",
              error: "Invalid response from server",
            });
          }
        } catch (error: any) {
          failCount++;
          console.error(`Error sending email to ${cert.Email}:`, error);
          logs.push({
            name: cert.Name,
            email: cert.Email,
            status: "Failed",
            error: error.message,
          });

          // Show individual failure notification
          notifications.show({
            title: "Email Failed",
            message: `Failed to send certificate to ${cert.Name} (${cert.Email})`,
            color: "red",
            autoClose: 3000,
          });
        }

        // Update progress
        const progressValue = ((i + 1) / totalEmails) * 100;
        setProgress(progressValue);
        setProgressMessage(
          `Sending ${i + 1} of ${totalEmails} certificates...`,
        );

        // Add delay to prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Final completion notification
      if (failCount === 0) {
        notifications.show({
          title: "All Certificates Sent!",
          message: `Successfully sent all ${totalEmails} certificates`,
          color: "green",
          autoClose: 5000,
        });
      } else {
        notifications.show({
          title: "Batch Complete",
          message: `Sent ${successCount} of ${totalEmails} certificates. Check individual notifications for failures.`,
          color: "orange",
          autoClose: 7000,
        });
      }

      // Store email logs
      setEmailLogs(logs);
      setMailSendingComplete(true);
    } catch (error: any) {
      console.error("Failed to send emails:", error);
      notifications.show({
        title: "Error",
        message: "Failed to process email batch",
        color: "red",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
      setProgress(0);
      setProgressMessage("");
    }
  };

  // const handleBulkUpload = async () => {
  //   setIsLoading(true);
  //   setProgress(0);
  //   setProgressMessage("Uploading certificates to database...");

  //   try {
  //     // Build payload and log it so we can inspect cpeCount/cpeHours before sending
  //     const payload = {
  //       certificates: generatedCertificates.map(cert => ({
  //         ...cert,
  //         eventData: {
  //           eventName,
  //           eventType,
  //           startDate,
  //           endDate: completionDate,
  //           completionDate,
  //           approvedBy: "Think Cloudly",
  //           cpeCount: cert.cpeCount,
  //           cpeHours: cert.cpeHours
  //         }
  //       }))
  //     };

  //     // Debug log payload (only first 10 certs to avoid excess output)
  //     console.log('handleBulkUpload - payload.certificates (sample):', payload.certificates.slice(0, 10));

  //     const response = await ApiService.getInstance().post("/certificate/bulk-upload", payload);

  //     if (response.data.success) {
  //       // UPDATE: Set database upload state and lock event details
  //       setIsUploadedToDatabase(true);
  //       setIsEventDetailsLocked(true);

  //       notifications.show({
  //         title: "Upload Complete",
  //         message: `Successfully uploaded ${response.data.uploaded} certificates to database. You can now send emails.`,
  //         color: "green",
  //       });
  //     } else {
  //       throw new Error(response.data.error || 'Failed to upload certificates');
  //     }
  //   } catch (error: any) {
  //     notifications.show({
  //       title: "Upload Error",
  //       message: error.message || "Failed to upload certificates to database",
  //       color: "red",
  //     });
  //   } finally {
  //     setIsLoading(false);
  //     setShowConfirmModal(false);
  //   }
  // };

  // REPLACE the handleBulkUpload function in Certification.tsx

  const handleBulkUpload = async () => {
    setIsLoading(true);
    setProgress(0);
    setProgressMessage("Uploading certificates to database...");

    try {
      // Build payload - send certificates directly without eventData wrapper
      const payload = {
        certificates: generatedCertificates.map((cert) => ({
          SerialNumber: cert.SerialNumber,
          Name: cert.Name,
          Email: cert.Email,
          EventName: cert.EventName,
          EventType: cert.EventType,
          StartDate: cert.StartDate,
          EndDate: cert.EndDate,
          ApprovedBy: cert.ApprovedBy || "Think Cloudly",
          CertificateNumber: cert.CertificateNumber,
          cpeCount: cert.cpeCount || "",
          cpeHours: cert.cpeHours || "",
        })),
      };

      // Debug log payload (only first 3 certs to avoid excess output)
      // console.log('handleBulkUpload - payload structure:', {
      //   certificatesCount: payload.certificates.length,
      //   sampleCertificates: payload.certificates.slice(0, 3)
      // });

      // Validate payload before sending
      if (!payload.certificates || payload.certificates.length === 0) {
        throw new Error("No certificates to upload");
      }

      // Check if any BOOTCAMP-CPE certificates are missing CPE data
      const missingCpeData = payload.certificates.filter(
        (cert) =>
          cert.EventType === "BOOTCAMP-CPE" &&
          (!cert.cpeCount || !cert.cpeHours),
      );

      if (missingCpeData.length > 0) {
        console.warn(
          "Warning: Some BOOTCAMP-CPE certificates missing CPE data:",
          missingCpeData,
        );
      }

      const response = await ApiService.getInstance().post(
        "/certificate/bulk-upload",
        payload,
      );

      if (response.data.success) {
        // UPDATE: Set database upload state and lock event details
        setIsUploadedToDatabase(true);
        setIsEventDetailsLocked(true);

        notifications.show({
          title: "Upload Complete",
          message: `Successfully uploaded ${response.data.uploaded} certificates to database. You can now send emails.`,
          color: "green",
        });

        // console.log('✓ Bulk upload successful:', {
        //   uploaded: response.data.uploaded,
        //   total: response.data.total,
        //   errors: response.data.errors
        // });
      } else {
        throw new Error(response.data.error || "Failed to upload certificates");
      }
    } catch (error: any) {
      console.error("Bulk upload error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
      });

      notifications.show({
        title: "Upload Error",
        message:
          error.response?.data?.error ||
          error.message ||
          "Failed to upload certificates to database",
        color: "red",
      });
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };
  const handleConfirmAction = () => {
    if (confirmAction === "upload") {
      handleBulkUpload();
    } else {
      handleBulkEmailSend();
    }
  };

  const handleReset = () => {
    setCurrentStep("upload");
    setUploadedData([]);
    setGeneratedCertificates([]);
    setEventType("");
    setEventName("");
    setStartDate("");
    setCompletionDate("");
    setViewedCertificate(null);
    setProgress(0);
    setProgressMessage("");
    setCountry("");
    setTempCountry("");
    // Reset new states
    setIsUploadedToDatabase(false);
    setIsEventDetailsLocked(false);
    setEmailLogs([]);
    setMailSendingComplete(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle opening edit modal - UPDATED to check if locked
  const handleOpenEditModal = () => {
    if (isEventDetailsLocked) {
      notifications.show({
        title: "Event Details Locked",
        message: "Event details cannot be edited after uploading to database",
        color: "orange",
      });
      return;
    }

    setTempEventType(eventType);
    setTempEventName(eventName);
    setTempStartDate(startDate);
    setTempCompletionDate(completionDate);
    setTempCpeCount(cpeCount);
    setTempCpeHours(cpeHours);
    setShowEditModal(true);
    setTempCountry(country);
  };

  // Handle saving edited details
  const handleSaveEditedDetails = () => {
    setEventType(tempEventType);
    setEventName(tempEventName);
    setStartDate(tempStartDate);
    setCompletionDate(tempCompletionDate);
    setCpeCount(tempCpeCount);
    setCpeHours(tempCpeHours);
    setShowEditModal(false);
    setCountry(tempCountry);

    notifications.show({
      title: "Details Updated",
      message: "Event details have been updated successfully",
      color: "green",
    });
  };

  // Render event details card - UPDATED to show lock status
  const renderEventDetailsCard = () => (
    <Card shadow="sm" padding="md" radius="md" withBorder mb="md">
      <Group justify="space-between" mb="md">
        <Group>
          <Title order={4}>Event Details</Title>
          {isEventDetailsLocked && (
            <Badge color="red" variant="filled" size="sm">
              Locked
            </Badge>
          )}
        </Group>
        <ActionIcon
          variant="light"
          color={isEventDetailsLocked ? "gray" : "blue"}
          onClick={handleOpenEditModal}
          size="sm"
          disabled={isEventDetailsLocked}
        >
          <IconEdit size={16} />
        </ActionIcon>
      </Group>
      <Stack gap="xs">
        <Group>
          <Text fw={500} size="sm">
            Event Type:
          </Text>
          <Badge variant="filled" color="blue">
            {eventType || "Not set"}
          </Badge>
        </Group>
        <Group>
          <Text fw={500} size="sm">
            Event Name:
          </Text>
          <Text size="sm">{eventName || "Not set"}</Text>
        </Group>
        {startDate && (
          <Group>
            <Text fw={500} size="sm">
              Start Date:
            </Text>
            <Text size="sm">
              {startDate ? formatDateToMMDDYYYY(startDate) : "Not set"}
            </Text>
          </Group>
        )}
        <Group>
          <Text fw={500} size="sm">
            Completion Date:
          </Text>
          <Text size="sm">
            {completionDate ? formatDateToMMDDYYYY(completionDate) : "Not set"}
          </Text>
        </Group>
        {eventType === "SEMINAR" && country && (
          <Group>
            <Text fw={500} size="sm">
              Country:
            </Text>
            <Text size="sm">{country}</Text>
          </Group>
        )}
        {isUploadedToDatabase && (
          <Group>
            <Text fw={500} size="sm" c="green">
              Database Status:
            </Text>
            <Badge color="green" variant="filled">
              Uploaded
            </Badge>
          </Group>
        )}
        {(eventType === "BOOTCAMP-CPE" || eventType === "CAPSULE COURSE") && (
          <Group>
            <Text fw={500} size="sm">
              CPE:
            </Text>
            <Text size="sm">
              {cpeCount ? `${cpeCount} CPE` : "Not set"} —{" "}
              {cpeHours ? `${cpeHours}` : "Not set"}
            </Text>
          </Group>
        )}
      </Stack>
    </Card>
  );

  return (
    <div className="certification-container">
      <div className="content-wrapper">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={2}>Certificate Management</Title>
            <Badge color="blue" variant="light">
              Step{" "}
              {currentStep === "upload"
                ? "1"
                : currentStep === "form"
                  ? "2"
                  : currentStep === "generate"
                    ? "3"
                    : "4"}{" "}
              of 4
            </Badge>
          </Group>

          {/* Show Event Details Card on all steps except upload */}
          {currentStep !== "upload" &&
            (eventType || eventName || completionDate) &&
            renderEventDetailsCard()}

          {/* Step 1: File Upload */}
          {currentStep === "upload" && (
            <Stack>
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Upload Student Data"
                color="blue"
              >
                Upload an Excel/CSV file containing student names and email
                addresses to generate certificates.
              </Alert>

              <Group>
                <Button
                  leftSection={<IconUpload size={16} />}
                  onClick={handleButtonClick}
                  variant="filled"
                >
                  Upload CSV/Excel File
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadCSVTemplate}
                  leftSection={<IconDownload size={16} />}
                >
                  Download Template
                </Button>
              </Group>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
              />

              {uploadedData.length > 0 && (
                <Alert
                  icon={<IconCheck size={16} />}
                  title="File Loaded"
                  color="green"
                >
                  Successfully loaded {uploadedData.length} students. Click Next
                  to continue.
                </Alert>
              )}
            </Stack>
          )}

          {/* Step 2: Event Details Form */}
          {currentStep === "form" && (
            <Stack>
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Event Details"
                color="blue"
              >
                Fill in the event details for certificate generation.
              </Alert>

              <Select
                label="Event Type"
                placeholder="Select event type"
                value={eventType}
                onChange={(value) => setEventType(value || "")}
                data={[
                  "BOOTCAMP",
                  "BOOTCAMP-CPE",
                  "CAPSULE COURSE",
                  "WEBINAR",
                  "COURSE COMPLETION",
                  "SEMINAR",
                ]}
                required
              />

              <TextInput
                label="Event Name"
                placeholder="Enter event name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                required
              />

              {(eventType === "BOOTCAMP" ||
                eventType === "COURSE COMPLETION" ||
                eventType === "BOOTCAMP-CPE" || eventType === "CAPSULE COURSE") && (
                <TextInput
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              )}

              {(eventType === "BOOTCAMP-CPE" || eventType === "CAPSULE COURSE") && (
                <>
                  <TextInput
                    label="CPE Count"
                    placeholder="Enter CPE Count (e.g. 16)"
                    value={cpeCount}
                    onChange={(e) => setCpeCount(e.target.value)}
                    required
                  />
                  <TextInput
                    label="CPE Hours"
                    placeholder="Enter CPE Hours (e.g. 16)"
                    value={cpeHours}
                    onChange={(e) => setCpeHours(e.target.value)}
                    required
                  />
                </>
              )}

              <TextInput
                label="Completion Date"
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                required
              />
              {eventType === "SEMINAR" && (
                <Select
                  label="Country"
                  placeholder="Select country"
                  value={country}
                  onChange={(value) => setCountry(value || "")}
                  data={COUNTRIES}
                  searchable
                  required
                />
              )}
              <Group>
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep("upload")}
                >
                  Back
                </Button>
                <Button
                  onClick={handleGenerateCertificates}
                  disabled={!eventType || !eventName || !completionDate}
                >
                  Generate Certificates
                </Button>
              </Group>
            </Stack>
          )}

          {/* Step 3: Loading */}
          {currentStep === "generate" && (
            <Stack align="center">
              <Loader size="xl" />
              <Text>Generating certificates...</Text>
              {progress > 0 && (
                <>
                  <Progress
                    value={progress}
                    size="lg"
                    style={{ width: "100%" }}
                  />
                  <Text size="sm" c="dimmed">
                    {progressMessage}
                  </Text>
                </>
              )}
            </Stack>
          )}
          {/* Step 4: Generated Certificates Table */}
          {currentStep === "table" && (
            <Stack>
              <Group justify="space-between">
                <Title order={3}>
                  Generated Certificates ({generatedCertificates.length})
                </Title>
                <Group>
                  <Button
                    leftSection={<IconDatabase size={16} />}
                    onClick={() => {
                      setConfirmAction("upload");
                      setShowConfirmModal(true);
                    }}
                    variant="filled"
                    color="blue"
                    disabled={isUploadedToDatabase}
                  >
                    {isUploadedToDatabase
                      ? "Uploaded to Database"
                      : "Upload to Database"}
                  </Button>
                  <Button
                    leftSection={<IconMail size={16} />}
                    onClick={() => {
                      setConfirmAction("send");
                      setShowConfirmModal(true);
                    }}
                    variant="filled"
                    color="green"
                    disabled={!isUploadedToDatabase}
                  >
                    Send All Emails
                  </Button>
                  <Button
                    leftSection={<IconRefresh size={16} />}
                    onClick={handleReset}
                    variant="outline"
                  >
                    Start Over
                  </Button>
                </Group>
              </Group>

              {/* Status Alert */}
              {!isUploadedToDatabase && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Upload Required"
                  color="orange"
                >
                  Please upload certificates to database before sending emails.
                </Alert>
              )}

              {isUploadedToDatabase && (
                <Alert
                  icon={<IconCheck size={16} />}
                  title="Ready to Send"
                  color="green"
                >
                  Certificates are uploaded to database. You can now send
                  emails.
                </Alert>
              )}

              <div style={{ overflowX: "auto" }}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Certificate Number</Table.Th>
                      <Table.Th>Start Date</Table.Th>
                      <Table.Th>Completion Date</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {generatedCertificates.map((cert) => {
                      // Determine the status for this certificate
                      const emailLog = emailLogs.find(
                        (log) => log.email === cert.Email,
                      );
                      const status = emailLog?.status || "Pending";

                      return (
                        <Table.Tr key={cert.SerialNumber}>
                          <Table.Td>{cert.Name}</Table.Td>
                          <Table.Td>{cert.Email}</Table.Td>
                          <Table.Td>
                            <Text size="sm" ff="monospace">
                              {cert.CertificateNumber}
                            </Text>
                          </Table.Td>
                          <Table.Td>{cert.StartDate}</Table.Td>
                          <Table.Td>{cert.EndDate}</Table.Td>
                          <Table.Td>
                            {status === "Sent" ? (
                              <Tooltip
                                label={`Message ID: ${emailLog?.messageId}`}
                              >
                                <Badge color="green">Sent</Badge>
                              </Tooltip>
                            ) : status === "Failed" ? (
                              <Tooltip
                                label={emailLog?.error || "Failed to send"}
                              >
                                <Badge color="red">Failed</Badge>
                              </Tooltip>
                            ) : (
                              <Badge color="gray">Pending</Badge>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => handleViewCertificate(cert)}
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                              <Tooltip
                                label={
                                  !isUploadedToDatabase
                                    ? "Upload to database first"
                                    : "Download certificate"
                                }
                                disabled={isUploadedToDatabase}
                              >
                                <ActionIcon
                                  variant="light"
                                  color="green"
                                  onClick={() =>
                                    handleDownloadCertificate(cert)
                                  }
                                  disabled={!isUploadedToDatabase}
                                  style={{
                                    opacity: !isUploadedToDatabase ? 0.5 : 1,
                                    cursor: !isUploadedToDatabase
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <IconDownload size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip
                                label={
                                  !isUploadedToDatabase
                                    ? "Upload to database first"
                                    : "Send email"
                                }
                                disabled={isUploadedToDatabase}
                              >
                                <ActionIcon
                                  variant="light"
                                  color="orange"
                                  onClick={() => handleSendEmail(cert)}
                                  loading={isLoading}
                                  disabled={!isUploadedToDatabase}
                                  style={{
                                    opacity: !isUploadedToDatabase ? 0.5 : 1,
                                    cursor: !isUploadedToDatabase
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <IconMail size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            </Stack>
          )}
        </Card>

        {/* Certificate Preview Modal */}
        <Modal
          opened={!!viewedCertificate}
          onClose={() => setViewedCertificate(null)}
          title="Certificate Preview"
          size="auto"
          centered
        >
          {viewedCertificate}
        </Modal>

        {/* Edit Event Details Modal */}
        <Modal
          opened={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Event Details"
          centered
        >
          <Stack>
            <Select
              label="Event Type"
              placeholder="Select event type"
              value={tempEventType}
              onChange={(value) => setTempEventType(value || "")}
              data={[
                "BOOTCAMP",
                "BOOTCAMP-CPE",
                 "CAPSULE COURSE",
                "WEBINAR",
                "COURSE COMPLETION",
                "SEMINAR",
              ]}
              required
            />

            <TextInput
              label="Event Name"
              placeholder="Enter event name"
              value={tempEventName}
              onChange={(e) => setTempEventName(e.target.value)}
              required
            />
            <TextInput
              label="Start Date"
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              required
            />

            <TextInput
              label="Completion Date"
              type="date"
              value={tempCompletionDate}
              onChange={(e) => setTempCompletionDate(e.target.value)}
              required
            />
            {tempEventType === "SEMINAR" && (
              <Select
                label="Country"
                placeholder="Select country"
                value={tempCountry}
                onChange={(value) => setTempCountry(value || "")}
                data={COUNTRIES}
                searchable
                required
              />
            )}

            {(tempEventType === "BOOTCAMP-CPE" || tempEventType === "CAPSULE COURSE") && (
              <>
                <TextInput
                  label="CPE Count"
                  placeholder="Enter CPE Count (e.g. 16)"
                  value={tempCpeCount}
                  onChange={(e) => setTempCpeCount(e.target.value)}
                  required
                />
                <TextInput
                  label="CPE Hours"
                  placeholder="Enter CPE Hours (e.g. 16)"
                  value={tempCpeHours}
                  onChange={(e) => setTempCpeHours(e.target.value)}
                  required
                />
              </>
            )}

            <Group justify="flex-end">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditedDetails}
                disabled={
                  !tempEventType ||
                  !tempEventName ||
                  !tempStartDate ||
                  !tempCompletionDate
                }
              >
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          opened={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title={
            confirmAction === "upload"
              ? "Upload to Database"
              : "Send Email Certificates"
          }
          centered
        >
          <Stack>
            <Text>
              {confirmAction === "upload"
                ? `Are you sure you want to upload ${generatedCertificates.length} certificates to the database?`
                : `Are you sure you want to send ${generatedCertificates.length} certificates via email?`}
            </Text>
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAction}
                loading={isLoading}
                color={confirmAction === "upload" ? "blue" : "green"}
              >
                {confirmAction === "upload" ? "Upload" : "Send Emails"}
              </Button>
            </Group>
          </Stack>
        </Modal>
        {/* Loading Overlay */}
        {isLoading && !mailSendingComplete && (
          <Modal
            opened={true}
            onClose={() => {}}
            withCloseButton={false}
            centered
          >
            <Stack align="center">
              <Loader size="xl" />
              <Text>{progressMessage}</Text>
              <Progress value={progress} size="lg" style={{ width: "100%" }} />
              <Text size="sm" c="dimmed">
                {Math.round(progress)}% Complete
              </Text>
            </Stack>
          </Modal>
        )}
      </div>
      <Outlet />
    </div>
  );
};

export default Certification;
